import React, { useState, useEffect } from "react";
import {
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Select,
} from "@material-ui/core";
import { Formik, Form, Field } from "formik";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ModalUsers from "../ModalUsers";
import usePlans from "../../hooks/usePlans";
import api from "../../services/api";
import { head, isArray } from "lodash";
import moment from "moment";
import { i18n } from "../../translate/i18n";
import { toast } from "sonner";

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    borderRadius: 14,
  },
  dialogTitle: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 700,
    fontSize: 18,
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
    padding: theme.spacing(2, 3),
  },
  dialogContent: {
    padding: theme.spacing(2, 3),
  },
  dialogActions: {
    padding: theme.spacing(2, 3),
    gap: theme.spacing(1),
  },
  textfield: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontFamily: '"Inter", "Poppins", sans-serif',
      "& fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#E2E8F0",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "#CBD5E1",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0B3C84",
      },
    },
    "& .MuiInputLabel-outlined": {
      fontFamily: '"Inter", "Poppins", sans-serif',
      "&.Mui-focused": {
        color: "#0B3C84",
      },
    },
  },
  selectContainer: {
    width: "100%",
    "& .MuiOutlinedInput-root": {
      borderRadius: 10,
      fontFamily: '"Inter", "Poppins", sans-serif',
      "& fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#E2E8F0",
      },
      "&:hover fieldset": {
        borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "#CBD5E1",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0B3C84",
      },
    },
    "& .MuiInputLabel-outlined": {
      fontFamily: '"Inter", "Poppins", sans-serif',
      "&.Mui-focused": {
        color: "#0B3C84",
      },
    },
    "& .MuiSelect-select": {
      fontFamily: '"Inter", "Poppins", sans-serif',
    },
  },
  menuItem: {
    fontFamily: '"Inter", "Poppins", sans-serif',
  },
  buttonCancel: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 600,
    fontSize: 14,
    textTransform: "none",
    borderRadius: 10,
    padding: "8px 24px",
    borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.12)" : "#E2E8F0",
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
    "&:hover": {
      borderColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.2)" : "#CBD5E1",
      backgroundColor: theme.palette.type === "dark" ? "rgba(255,255,255,0.05)" : "#F8FAFC",
    },
  },
  buttonSave: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 600,
    fontSize: 14,
    textTransform: "none",
    borderRadius: 10,
    padding: "8px 24px",
    backgroundColor: "#0B3C84",
    color: "#FFFFFF",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#082D63",
      boxShadow: "0 2px 8px rgba(11,60,132,0.3)",
    },
  },
  buttonDelete: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 600,
    fontSize: 14,
    textTransform: "none",
    borderRadius: 10,
    padding: "8px 24px",
    backgroundColor: theme.palette.error.main,
    color: "#FFFFFF",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: theme.palette.error.dark,
      boxShadow: "0 2px 8px rgba(211,47,47,0.3)",
    },
  },
  buttonSecondary: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontWeight: 600,
    fontSize: 14,
    textTransform: "none",
    borderRadius: 10,
    padding: "8px 24px",
    backgroundColor: "#0B3C84",
    color: "#FFFFFF",
    boxShadow: "none",
    "&:hover": {
      backgroundColor: "#082D63",
      boxShadow: "0 2px 8px rgba(11,60,132,0.3)",
    },
  },
  buttonsContainer: {
    display: "flex",
    gap: theme.spacing(1.5),
    flexWrap: "wrap",
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#E2E8F0"}`,
  },
}));

const CompanyFormModal = ({ open, onClose, initialValue, onSubmit, onDelete, loading }) => {
  const classes = useStyles();
  const [plans, setPlans] = useState([]);
  const [modalUser, setModalUser] = useState(false);
  const [firstUser, setFirstUser] = useState({});

  const [record, setRecord] = useState({
    name: "",
    email: "",
    phone: "",
    planId: "",
    status: true,
    dueDate: "",
    recurrence: "",
    password: "",
    document: "",
    ...initialValue,
  });

  const { list: listPlans } = usePlans();

  useEffect(() => {
    async function fetchData() {
      const list = await listPlans();
      setPlans(list);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open && initialValue) {
      setRecord((prev) => {
        const value = { ...initialValue };
        if (moment(value.dueDate).isValid()) {
          value.dueDate = moment(value.dueDate).format("YYYY-MM-DD");
        }
        return {
          ...prev,
          ...value,
        };
      });
    } else if (open && !initialValue) {
      setRecord({
        name: "",
        email: "",
        phone: "",
        planId: "",
        status: true,
        dueDate: "",
        recurrence: "",
        password: "",
        document: "",
      });
    }
  }, [open, initialValue]);

  const handleSubmit = async (data) => {
    if (data.dueDate === "" || moment(data.dueDate).isValid() === false) {
      data.dueDate = null;
    }
    await onSubmit(data);
  };

  const handleOpenModalUsers = async () => {
    if (!record.id) return;
    try {
      const { data } = await api.get("/users/list", {
        params: {
          companyId: record.id,
        },
      });
      if (isArray(data) && data.length) {
        setFirstUser(head(data));
      }
      setModalUser(true);
    } catch (e) {
      toast.error(e?.message || String(e));
    }
  };

  const handleCloseModalUsers = () => {
    setFirstUser({});
    setModalUser(false);
  };

  const incrementDueDate = (currentRecord) => {
    const data = { ...currentRecord };
    if (data.dueDate !== "" && data.dueDate !== null) {
      switch (data.recurrence) {
        case "MENSAL":
          data.dueDate = moment(data.dueDate)
            .add(1, "month")
            .format("YYYY-MM-DD");
          break;
        case "BIMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(2, "month")
            .format("YYYY-MM-DD");
          break;
        case "TRIMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(3, "month")
            .format("YYYY-MM-DD");
          break;
        case "SEMESTRAL":
          data.dueDate = moment(data.dueDate)
            .add(6, "month")
            .format("YYYY-MM-DD");
          break;
        case "ANUAL":
          data.dueDate = moment(data.dueDate)
            .add(12, "month")
            .format("YYYY-MM-DD");
          break;
        default:
          break;
      }
    }
    return data;
  };

  const handleClose = () => {
    setRecord({
      name: "",
      email: "",
      phone: "",
      planId: "",
      status: true,
      dueDate: "",
      recurrence: "",
      password: "",
      document: "",
    });
    onClose();
  };

  return (
    <>
      <ModalUsers
        userId={firstUser.id}
        companyId={record.id}
        open={modalUser}
        onClose={handleCloseModalUsers}
      />
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          className: classes.dialogPaper,
        }}
      >
        <DialogTitle className={classes.dialogTitle}>
          {record.id !== undefined
            ? "Editar Empresa"
            : "Cadastrar Empresa"}
        </DialogTitle>
        <Formik
          enableReinitialize
          initialValues={record}
          onSubmit={(values, { resetForm }) => {
            setTimeout(() => {
              handleSubmit(values);
              resetForm();
            }, 500);
          }}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <DialogContent className={classes.dialogContent} dividers>
                <Grid spacing={3} container>
                  {/* Primeira linha - Informações básicas */}
                  <Grid xs={12} sm={6} md={4} item>
                    <Field
                      as={TextField}
                      label={i18n.t("compaies.table.name")}
                      name="name"
                      variant="outlined"
                      className={classes.textfield}
                      margin="dense"
                      fullWidth
                      autoFocus
                    />
                  </Grid>
                  <Grid xs={12} sm={6} md={4} item>
                    <Field
                      as={TextField}
                      label={i18n.t("compaies.table.email")}
                      name="email"
                      variant="outlined"
                      className={classes.textfield}
                      margin="dense"
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid xs={12} sm={6} md={4} item>
                    <Field
                      as={TextField}
                      label={i18n.t("compaies.table.password")}
                      name="password"
                      type="password"
                      variant="outlined"
                      className={classes.textfield}
                      margin="dense"
                      fullWidth
                    />
                  </Grid>

                  {/* Segunda linha - Contato e Plano */}
                  <Grid xs={12} sm={6} md={4} item>
                    <Field
                      as={TextField}
                      label={i18n.t("compaies.table.phone")}
                      name="phone"
                      variant="outlined"
                      className={classes.textfield}
                      margin="dense"
                      fullWidth
                    />
                  </Grid>
                  <Grid xs={12} sm={6} md={4} item>
                    <FormControl margin="dense" variant="outlined" fullWidth className={classes.selectContainer}>
                      <InputLabel htmlFor="plan-selection">{i18n.t("compaies.table.plan")}</InputLabel>
                      <Field
                        as={Select}
                        id="plan-selection"
                        label={i18n.t("compaies.table.plan")}
                        labelId="plan-selection-label"
                        name="planId"
                        margin="dense"
                        required
                      >
                        {plans.map((plan, key) => (
                          <MenuItem className={classes.menuItem} key={key} value={plan.id}>
                            {plan.name}
                          </MenuItem>
                        ))}
                      </Field>
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={6} md={4} item>
                    <FormControl margin="dense" variant="outlined" fullWidth className={classes.selectContainer}>
                      <InputLabel htmlFor="status-selection">{i18n.t("compaies.table.active")}</InputLabel>
                      <Field
                        as={Select}
                        id="status-selection"
                        label={i18n.t("compaies.table.active")}
                        labelId="status-selection-label"
                        name="status"
                        margin="dense"
                      >
                        <MenuItem className={classes.menuItem} value={true}>{i18n.t("compaies.table.yes")}</MenuItem>
                        <MenuItem className={classes.menuItem} value={false}>{i18n.t("compaies.table.no")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  {/* Terceira linha - Documento e Vencimento */}
                  <Grid xs={12} sm={6} md={4} item>
                    <Field
                      as={TextField}
                      label={i18n.t("compaies.table.document")}
                      name="document"
                      variant="outlined"
                      className={classes.textfield}
                      margin="dense"
                      fullWidth
                    />
                  </Grid>
                  <Grid xs={12} sm={6} md={4} item>
                    <FormControl variant="outlined" fullWidth>
                      <Field
                        as={TextField}
                        label={i18n.t("compaies.table.dueDate")}
                        type="date"
                        name="dueDate"
                        InputLabelProps={{
                          shrink: true,
                        }}
                        variant="outlined"
                        fullWidth
                        margin="dense"
                        className={classes.textfield}
                      />
                    </FormControl>
                  </Grid>
                  <Grid xs={12} sm={6} md={4} item>
                    <FormControl margin="dense" variant="outlined" fullWidth className={classes.selectContainer}>
                      <InputLabel htmlFor="recorrencia-selection">
                        {i18n.t("compaies.table.recurrence")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label="Recorrência"
                        labelId="recorrencia-selection-label"
                        id="recurrence"
                        name="recurrence"
                        margin="dense"
                      >
                        <MenuItem className={classes.menuItem} value="MENSAL">{i18n.t("compaies.table.monthly")}</MenuItem>
                        <MenuItem className={classes.menuItem} value="BIMESTRAL">{i18n.t("compaies.table.bimonthly")}</MenuItem>
                        <MenuItem className={classes.menuItem} value="TRIMESTRAL">{i18n.t("compaies.table.quarterly")}</MenuItem>
                        <MenuItem className={classes.menuItem} value="SEMESTRAL">{i18n.t("compaies.table.semester")}</MenuItem>
                        <MenuItem className={classes.menuItem} value="ANUAL">{i18n.t("compaies.table.yearly")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  {/* Botões de ação - apenas quando editando */}
                  {record.id !== undefined && (
                    <Grid xs={12} item>
                      <div className={classes.buttonsContainer}>
                        <ButtonWithSpinner
                          loading={loading}
                          onClick={() => onDelete(record)}
                          variant="contained"
                          color="secondary"
                          className={classes.buttonDelete}
                        >
                          {i18n.t("compaies.table.delete")}
                        </ButtonWithSpinner>
                        <ButtonWithSpinner
                          loading={loading}
                          onClick={() => {
                            const updated = incrementDueDate(values);
                            Object.keys(updated).forEach((key) => {
                              setFieldValue(key, updated[key]);
                            });
                          }}
                          variant="contained"
                          color="primary"
                          className={classes.buttonSecondary}
                        >
                          {i18n.t("compaies.table.dueDate")}
                        </ButtonWithSpinner>
                        <ButtonWithSpinner
                          loading={loading}
                          onClick={() => handleOpenModalUsers()}
                          variant="contained"
                          color="primary"
                          className={classes.buttonSecondary}
                        >
                          {i18n.t("compaies.table.user")}
                        </ButtonWithSpinner>
                      </div>
                    </Grid>
                  )}
                </Grid>
              </DialogContent>
              <DialogActions className={classes.dialogActions}>
                <Button
                  onClick={() => {
                    setRecord({
                      name: "",
                      email: "",
                      phone: "",
                      planId: "",
                      status: true,
                      dueDate: "",
                      recurrence: "",
                      password: "",
                      document: "",
                    });
                    setFieldValue("name", "");
                    setFieldValue("email", "");
                    setFieldValue("phone", "");
                    setFieldValue("planId", "");
                    setFieldValue("status", true);
                    setFieldValue("dueDate", "");
                    setFieldValue("recurrence", "");
                    setFieldValue("password", "");
                    setFieldValue("document", "");
                  }}
                  variant="outlined"
                  className={classes.buttonCancel}
                  disabled={loading}
                >
                  {i18n.t("compaies.table.clear")}
                </Button>
                <Button
                  onClick={handleClose}
                  variant="outlined"
                  className={classes.buttonCancel}
                  disabled={loading}
                >
                  {i18n.t("confirmationModal.buttons.cancel") || "Cancelar"}
                </Button>
                <ButtonWithSpinner
                  className={classes.buttonSave}
                  loading={loading}
                  type="submit"
                  variant="contained"
                  color="primary"
                >
                  {i18n.t("compaies.table.save")}
                </ButtonWithSpinner>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </>
  );
};

export default CompanyFormModal;

